import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonDropdown,
  Container,
  FlashbarProps,
  FormField,
  Header,
  KeyValuePairs,
  Modal,
  Pagination,
  SpaceBetween,
  Spinner,
  Table,
  Textarea,
  Toggle,
} from '@amzn/awsui-components-react-v3';
import { UsageAgreementForm } from 'src/components/datausageagreement/usageAgreementForm';
import { getReview } from 'src/api/notifications';
import { ReviewContent, ReviewerInfo, ReviewerPersona } from 'aws-sdk/clients/awsdlomni';
import { ExpandableSection } from '@amzn/awsui-components-react-v3/polaris';
import { getPhoneToolLink, toLocalTime } from 'src/commons/common';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { EmptyState } from 'src/commons/EmptyState';
import { ReviewStatus } from 'src/components/reviews/reviewStatus';
import { UsageAgreementDiff } from 'src/components/datausageagreement/usageAgreementDiff';
import { ReviewAgreements } from 'src/components/reviews/reviewAgreements';
import { ResourceNotFound } from 'src/commons/ResourceNotFound';
import { getDataAgreement } from 'src/api/d3a';
import {
  createAgreementUpdater,
  createNotificationHelpers,
  groupReviewersByResourceAgreement,
  isCustodianAction,
  renderValidationList,
  submitReviewsByAgreement,
  validateAgreements,
} from 'src/components/reviews/utils';
import { ReviewAgreementsResponseList } from 'src/components/reviews/types';
import { APPROVE, CONSUMER, CUSTODIAN, DENY, KEEP, NEEDS_MORE_DATA, RELINQUISH } from 'src/commons/constants';
import { createOwnerLink } from 'src/routes';
import { Link } from 'src/components/common/Link';
import { ReviewerUpdateModal } from 'src/components/reviews/reviewerUpdateModal';
import ChatBubble from '@amzn/awsui-chat-components-console/chat-bubble';
import { FORM_CONTEXTS_FOR_REVIEWS } from 'src/components/datausageagreement/utils';

export interface ReviewDashboardProps {
  catalogMap: any;
  setContentType: any;
  setNotifications: React.Dispatch<React.SetStateAction<FlashbarProps.MessageDefinition[]>>;
  match: any;
}

function ReviewDetails(props: ReviewDashboardProps) {
  const [loadingReview, setLoadingReview] = useState(false);
  const [review, setReview] = useState<ReviewContent>();
  const [visible, setVisible] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comment, setComment] = useState();
  const [showChangesOnly, setShowChangesOnly] = useState(true);
  const [schemas, setSchemas] = useState({});
  const [reviewersByAgreement, setReviewersByAgreement] = useState<ReviewAgreementsResponseList>([]);
  const { showError, showSuccess } = createNotificationHelpers(props.setNotifications);
  const [reviewerUpdateModalVisible, setReviewerUpdateModalVisible] = useState(false);

  const parsedValidationResults = useMemo(() => {
    try {
      const raw = review?.latestConsumerReviewRecord?.agreementValidationResult;
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }, [review?.latestConsumerReviewRecord?.agreementValidationResult]);

  const getValidationListForAgreement = (agreementId: string) => {
    const match = parsedValidationResults.find((r) => r.agreementId === agreementId);
    return match?.validationList ?? [];
  };

  useEffect(() => {
    handleRefresh();
  }, [props.match.params.ownerId]);

  const submitReviewWithAction = async (persona: ReviewerPersona, action: string) => {
    try {
      setIsSubmitting(true);
      await submitReviewsByAgreement(
        reviewersByAgreement?.length ? reviewersByAgreement : groupReviewersByResourceAgreement([review]),
        persona,
        comment,
        action,
      );
      showSuccess('Review submitted');
    } catch (err) {
      console.error(err);
      showError('Failed to submit review: ' + err.message);
      setVisible(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setLoadingReview(true);
      const review = await getReview({
        reviewId: props.match.params.reviewId,
      });

      const agreementSchemas = {};
      await Promise.all(
        review.review.resourceAgreementsBefore.map(async (agreement) => {
          const getDataAgreementOutput = await getDataAgreement({ id: agreement.agreementId });
          agreementSchemas[agreement.agreementId] = JSON.stringify(
            getDataAgreementOutput.dataAgreements[0]?.jsonSchema,
          );
        }),
      );
      setReview(review.review);
      setSchemas(agreementSchemas);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingReview(false);
    }
  };

  const historyItems = review?.reviewRecordsHistory ? Object.values(review?.reviewRecordsHistory).reverse() : [];

  const reviews = useMemo(() => [review], [review]);

  const { items: reviewRecordsHistoryItems, paginationProps } = useCollection(historyItems, {
    filtering: {
      noMatch: {},
      empty: <EmptyState title='No matches' subtitle='No resources found.' />,
    },
    pagination: { pageSize: 15 },
    sorting: {},
    selection: {},
    propertyFiltering: {
      filteringProperties: [
        {
          propertyLabel: 'Resource type',
          key: 'resourceType',
          groupValuesLabel: 'Types',
        },
      ],
    },
  });

  const generateReviewerList = (reviewerInfos: ReviewerInfo[], reviewerRole) => {
    let res = [];

    if (reviewerInfos == null) {
      return;
    }
    for (let reviewerInfo of reviewerInfos) {
      if (reviewerRole == reviewerInfo.reviewerRole) {
        let reviewer = reviewerInfo.reviewer;
        if (reviewer.gladstoneGroupId != null) {
          let item = <Link href={createOwnerLink(reviewer.gladstoneGroupId)}>{reviewer.gladstoneGroupId}</Link>;
          res.push(item);
        } else if (reviewer.gladstoneWorkspaceId != null) {
          let item = <Link href={createOwnerLink(reviewer.gladstoneWorkspaceId)}>{reviewer.gladstoneWorkspaceId}</Link>;
          res.push(item);
        } else if (reviewer.amazonEmployeeAlias != null) {
          let item = getPhoneToolLink(reviewer.amazonEmployeeAlias);
          res.push(item);
        }
      }
    }
    return <SpaceBetween size='s'>{res}</SpaceBetween>;
  };

  const handleSubmit = async () => {
    try {
      switch (selectedAction) {
        case 'keep':
          setIsSubmitting(true);
          try {
            if (!(await validateAgreements(reviewersByAgreement, createAgreementUpdater(setReviewersByAgreement)))) {
              return;
            }
            await submitReviewWithAction(CONSUMER, KEEP);
          } finally {
            setIsSubmitting(false);
          }
          break;
        case 'relinquish':
          await submitReviewWithAction(CONSUMER, RELINQUISH);
          break;
        case 'approve':
          await submitReviewWithAction(CUSTODIAN, APPROVE);
          break;
        case 'deny':
          await submitReviewWithAction(CUSTODIAN, DENY);
          break;
        case 'request data':
          await submitReviewWithAction(CUSTODIAN, NEEDS_MORE_DATA);
          break;
        default:
          console.error('Unknown action:', selectedAction);
          return;
      }
      setVisible(false);
      await handleRefresh();
    } catch (error) {
      console.error('Error submitting review:', error);
      showError('Error submitting review: ' + error.message);
    }
  };

  const handleClick = (e) => {
    setVisible(true);
    setSelectedAction(e.detail.id);
  };

  const onDismiss = () => {
    setVisible(false);
  };
  const handleCommentChange = (e) => {
    setComment(e.detail.value);
  };

  if (!review && !loadingReview) {
    return (
      <ResourceNotFound
        title='Review not found'
        subtitle="The provided review does not exist or you don't have permissions to see it."
      />
    );
  }

  return (
    <>
      <ReviewerUpdateModal
        {...props}
        visible={reviewerUpdateModalVisible}
        dismiss={() => {
          setReviewerUpdateModalVisible(false);
        }}
        reviewIds={[review?.reviewId]}
        refresh={handleRefresh}
      />
      <Modal
        visible={visible}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button variant='link' onClick={onDismiss} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button variant='primary' onClick={handleSubmit} loading={isSubmitting}>
                Submit {selectedAction}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header='Add comment'
        onDismiss={onDismiss}
      >
        <SpaceBetween size='m'>
          {selectedAction == 'keep' && (
            <ReviewAgreements
              setContentType={props.setContentType}
              match={props.match}
              reviews={reviews}
              reviewersByAgreement={reviewersByAgreement}
              setReviewersByAgreement={setReviewersByAgreement}
            />
          )}
          <FormField description='Add a comment or observations for the reviewer.' label='Comment'>
            <Textarea
              placeholder='Type your comment here...'
              value={comment}
              onChange={handleCommentChange}
              disabled={isSubmitting}
            />
          </FormField>
        </SpaceBetween>
      </Modal>
      {(review && (
        <SpaceBetween size='m'>
          <Header
            actions={
              <SpaceBetween key='actions' direction='horizontal' size='xs'>
                <Button
                  variant='normal'
                  onClick={() => {
                    setReviewerUpdateModalVisible(true);
                  }}
                >
                  Delegate review
                </Button>
                <ButtonDropdown
                  variant='primary'
                  items={[
                    { id: 'keep', text: 'Keep', description: 'Keep access' },
                    { id: 'relinquish', text: 'Relinquish', description: 'Relinquish access' },
                  ]}
                  onItemClick={handleClick}
                >
                  Reviewer actions
                </ButtonDropdown>
              </SpaceBetween>
            }
            description='Review and take action on resources assigned to you for this review period.'
            variant='h1'
          >
            Review details of {review.reviewId}
          </Header>
          <SpaceBetween size='l'>
            <React.Fragment key='.0'>
              <Container
                fitHeight={undefined}
                header={
                  <Header description='Summary details for the review.' variant='h2'>
                    Summary
                  </Header>
                }
              >
                <SpaceBetween size='l'>
                  <KeyValuePairs
                    columns={2}
                    items={
                      review?.reviewers == null
                        ? [
                            {
                              label: 'Review Season',
                              value: review.seasonId,
                            },
                            {
                              label: 'Resource namespace',
                              value: review.namespace,
                            },
                            {
                              label: 'Resource type',
                              value: review.resourceType,
                            },
                            {
                              label: 'Resource ID',
                              value: (
                                <Link external={true} href={review.link}>
                                  {review.resourceId}
                                </Link>
                              ),
                            },
                            {
                              label: 'Resource Name',
                              value: review.name,
                            },
                            {
                              label: 'Resource description',
                              value: review.description,
                            },
                            {
                              label: 'Review status',
                              value: <ReviewStatus status={review.status} />,
                            },
                          ]
                        : [
                            {
                              label: 'Review Season',
                              value: review.seasonId,
                            },
                            {
                              label: 'Resource namespace',
                              value: review.namespace,
                            },
                            {
                              label: 'Resource type',
                              value: review.resourceType,
                            },
                            {
                              label: 'Resource ID',
                              value: (
                                <Link external={true} href={review.link}>
                                  {review.resourceId}
                                </Link>
                              ),
                            },
                            {
                              label: 'Resource Name',
                              value: review.name,
                            },
                            {
                              label: 'Resource description',
                              value: review.description,
                            },
                            {
                              label: 'Review status',
                              value: <ReviewStatus status={review.status} />,
                            },
                            {
                              label: 'Eligible custodians',
                              value: generateReviewerList(review?.reviewers, 'CUSTODIAN'),
                            },
                            {
                              label: 'Eligible main reviewer',
                              value: generateReviewerList(review?.reviewers, 'MAIN_REVIEWER'),
                            },
                            {
                              label: 'Eligible additional reviewers',
                              value: generateReviewerList(review?.reviewers, 'ADDITIONAL_REVIEWER'),
                            },
                          ]
                    }
                  />
                </SpaceBetween>
              </Container>

              {(review && (
                <ExpandableSection headerText='Resource details'>
                  <UsageAgreementForm
                    key={'stable'}
                    schema={review.resourceTypeSchema}
                    formData={JSON.parse(review.resourceData) ?? {}}
                    setFormData={() => {}}
                    errors={[]}
                    setErrors={() => {}}
                    readOnly={true}
                    omniContext={{}}
                    textOnly={true}
                  />
                </ExpandableSection>
              )) || <Spinner size='large' />}

              {review && (
                <>
                  <Container>
                    <Header
                      variant='h3'
                      actions={
                        <ButtonDropdown
                          items={[
                            { id: 'approve', text: 'Approve' },
                            { id: 'deny', text: 'Deny' },
                            {
                              id: 'request data',
                              text: 'Request more data',
                              disabled: review.latestConsumerReviewRecord == null,
                            },
                          ]}
                          onItemClick={handleClick}
                        >
                          Custodian actions
                        </ButtonDropdown>
                      }
                    >
                      Last review submission
                    </Header>
                    {(review.latestConsumerReviewRecord && (
                      <SpaceBetween size='l'>
                        <KeyValuePairs
                          columns={2}
                          items={[
                            {
                              label: 'Reviewed by',
                              value: review.latestConsumerReviewRecord?.reviewedBy,
                            },
                            {
                              label: 'Comment',
                              value: review.latestConsumerReviewRecord?.comment,
                            },
                            {
                              label: 'Agreement override',
                              value: review.latestConsumerReviewRecord?.overrideAgreementsReason ?? '-',
                            },
                            {
                              label: 'Review action',
                              value: <ReviewStatus status={review.latestConsumerReviewRecord?.action} />,
                            },
                          ]}
                        />
                        <Toggle onChange={({ detail }) => setShowChangesOnly(detail.checked)} checked={showChangesOnly}>
                          Show changes only
                        </Toggle>
                        <SpaceBetween size={'m'} direction={'vertical'}>
                          {review.latestConsumerReviewRecord.resourceAgreements?.map((agreement) => (
                            <SpaceBetween size={'s'} direction={'vertical'} key={agreement.agreementId}>
                              <UsageAgreementDiff
                                schema={schemas[agreement.agreementId] ?? '{}'}
                                beforeFormData={JSON.parse(
                                  review.resourceAgreementsBefore.find(
                                    (before) => before.agreementId === agreement.agreementId,
                                  )?.agreementData ?? '{}',
                                )}
                                afterFormData={JSON.parse(agreement.agreementData) ?? {}}
                                errors={[]}
                                setErrors={(e) => {
                                  console.log(e);
                                }}
                                showChangesOnly={showChangesOnly}
                                contexts={FORM_CONTEXTS_FOR_REVIEWS}
                              />
                              {renderValidationList(getValidationListForAgreement(agreement.agreementId))}
                            </SpaceBetween>
                          ))}
                        </SpaceBetween>
                      </SpaceBetween>
                    )) || (
                      <EmptyState
                        title='Review not yet sumbitted'
                        subtitle='The reviewers have not submitted a decision yet.'
                      />
                    )}
                  </Container>
                </>
              )}

              <Table
                columnDefinitions={[
                  {
                    cell: (item) => (
                      <>
                        <ChatBubble
                          avatar={getPhoneToolLink(item.reviewedBy, true)}
                          type={isCustodianAction(item.action) ? 'incoming' : 'outgoing'}
                          ariaLabel={'test'}
                        >
                          <KeyValuePairs
                            items={[
                              {
                                label: toLocalTime(item.reviewedAt),
                                value: item.comment ?? <i>Submitted without comments</i>,
                              },
                            ]}
                          />
                        </ChatBubble>
                      </>
                    ),
                    header: 'Reviewer',
                    id: 'reviewer',
                    minWidth: 200,
                  },
                  {
                    cell: (item) => <ReviewStatus status={item.action} />,
                    header: 'Action',
                    id: 'action',
                  },
                  {
                    cell: (item) => <p>{item.overrideAgreementsReason ?? '-'}</p>,
                    header: 'Override form reason',
                    id: 'override',
                  },
                ]}
                empty={<EmptyState title='No history' subtitle='This review has not been submitted' />}
                header={<Header description='History of actions for this review.'>Review History</Header>}
                items={reviewRecordsHistoryItems}
                loadingText='Loading'
                pagination={<Pagination {...paginationProps} />}
                selectedItems={[]}
                trackBy='date'
              />
            </React.Fragment>
          </SpaceBetween>
        </SpaceBetween>
      )) || <Spinner size='large' />}
    </>
  );
}

export default ReviewDetails;
